// Phase 5.4 · 간편 상품 뷰
// - 최근 상품 · + 새 상품 등록 · 오늘 라이브 판매 상품 · 재고부족 상품 알림
// - [전체 상품 보기 / 상세 관리] → 기존 ProductsSection 렌더링
import React, { useEffect, useState } from 'react';
import { Plus, Package, AlertTriangle, ChevronRight, Loader2, Radio } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

export default function SimpleProducts({ goTo, onOpenDetailedSection, onOpenProductForm, onEditProduct }) {
  const [products, setProducts] = useState([]);
  const [liveProducts, setLiveProducts] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rP, rB, rOps] = await Promise.all([
          api.get('/seller/products', { params: { sort: 'created_desc', page: 1, limit: 6 } }),
          api.get('/seller/broadcasts/today').catch(() => ({ data: [] })),
          api.get('/seller/dashboard/ops-summary').catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        // Envelope: { items, total, ... } OR legacy array. Always coerce to array.
        const raw = rP.data;
        const items = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
        setProducts(items.slice(0, 6));
        setShortages(rOps.data?.stock_alerts || []);
        // Pick first today broadcast's live products (envelope has .items).
        const todays = Array.isArray(rB.data) ? rB.data : (rB.data?.items || []);
        if (todays.length > 0) {
          try {
            const rLP = await api.get(`/seller/broadcasts/${todays[0].schedule_id}/live-products`);
            if (alive) {
              const d = rLP.data;
              const items = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : [];
              setLiveProducts(items);
            }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 py-16 justify-center" data-testid="simple-products-loading"><Loader2 className="animate-spin" size={16} /> 불러오는 중…</div>;
  }

  return (
    <div className="space-y-5" data-testid="simple-products-section">
      {/* 헤더 + 새 상품 등록 큰 버튼 */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">상품 관리</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            상품
          </h2>
          <p className="mt-1 text-xs text-gray-500">최근 상품 확인 → 새 상품 등록 → 라이브 판매상품 선택.</p>
        </div>
        <button onClick={() => (onOpenProductForm ? onOpenProductForm() : onOpenDetailedSection?.())}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#FF3B30] text-white text-sm md:text-base font-black rounded-full shadow-lg shadow-[#FF3B30]/20 hover:bg-[#D32F2F]"
          data-testid="simple-products-new-btn">
          <Plus size={18} /> 새 상품 등록
        </button>
      </div>

      {/* 최근 상품 */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-4" data-testid="simple-products-recent">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-sm font-black tracking-widest uppercase">최근 등록 상품</p>
          <button onClick={onOpenDetailedSection} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="simple-products-all-btn">
            전체 상품 보기 →
          </button>
        </div>
        {products.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">아직 등록된 상품이 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="simple-products-grid">
            {(Array.isArray(products) ? products : []).map((p) => (
              <button key={p.product_id}
                onClick={(e) => {
                  // A4-1G-1A · 상품 카드 클릭 → 해당 상품 수정 modal OPEN (기존 Edit Modal REUSE).
                  // 우측 [전체 상품 보기 →] 링크와 명확히 분리 (그 링크는 별도 button).
                  e.stopPropagation();
                  if (onEditProduct) onEditProduct(p);
                  else onOpenDetailedSection?.();
                }}
                className="flex items-center gap-3 p-2 rounded border border-black/5 dark:border-white/5 hover:border-[#FF3B30]/30 text-left"
                data-testid={`simple-product-${p.product_id}`}>
                <SafeImg src={p.image || p.thumbnail || p.image_url} kind="product" alt="" className="w-14 h-14 rounded object-contain bg-black/5 dark:bg-white/5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500">{won(p.sale_price || p.price || 0)}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {p.created_at ? format(new Date(p.created_at), 'M/d', { locale: ko }) : ''}
                    {p.status && <span className="ml-1">· {p.status}</span>}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 오늘 라이브 판매 상품 */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-4" data-testid="simple-products-live">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-sm font-black tracking-widest uppercase">오늘 라이브 판매 상품</p>
          <button onClick={() => goTo('live')} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="simple-products-goto-live">
            <Radio size={12} className="inline mr-0.5" /> 라이브 관리 →
          </button>
        </div>
        {liveProducts.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">오늘 라이브 방송에 연결된 상품이 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="simple-products-live-grid">
            {(Array.isArray(liveProducts) ? liveProducts : []).slice(0, 8).map((p) => (
              <div key={p.product_id} className="flex items-center gap-2 p-2 rounded border border-black/5 dark:border-white/5">
                <SafeImg src={p.image || p.thumbnail} kind="product" alt="" className="w-10 h-10 rounded object-contain bg-black/5 dark:bg-white/5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500">{won(p.sale_price || p.price || 0)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 재고부족 상품 알림 */}
      {shortages.length > 0 && (
        <div className="rounded-lg border-2 border-amber-400/50 bg-amber-50/60 dark:bg-amber-500/10 p-4" data-testid="simple-products-shortage">
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              <p className="text-sm font-black tracking-widest uppercase text-amber-900 dark:text-amber-200">재고 부족 상품 ({shortages.length})</p>
            </div>
            <button onClick={() => goTo('inventory')} className="text-xs font-bold text-amber-800 dark:text-amber-200 hover:underline" data-testid="simple-products-goto-inventory">
              재고 관리 →
            </button>
          </div>
          <div className="space-y-1.5">
            {(Array.isArray(shortages) ? shortages : []).slice(0, 5).map((s) => (
              <div key={`${s.product_id}-${s.variant_key}`} className="flex items-center justify-between text-xs p-2 rounded border border-amber-500/20 bg-white/60 dark:bg-black/20" data-testid={`simple-products-shortage-${s.product_id}`}>
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-semibold">{s.product_name}</span>
                  {s.variant_key !== 'default' && <span className="text-gray-500"> · {s.variant_key}</span>}
                </span>
                <span className="text-[10px] font-bold text-gray-500 flex-shrink-0">
                  가능 {s.available_stock} · 부족 {s.unfulfilled_qty || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
